import { useState, useEffect } from 'react';
import { getProducts } from '../services/productService';
import toast from 'react-hot-toast';

function InventoryManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [allocations, setAllocations] = useState({
    amazonUAE: 0,
    amazonSaudi: 0,
    amazonEgypt: 0,
    noonUAE: 0,
    noonSaudi: 0,
    noonEgypt: 0,
    jumia: 0,
    carrefourUAE: 0
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const fetchedProducts = await getProducts();
      console.log('Fetched products for inventory:', fetchedProducts);
      setProducts(fetchedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const totalStock = selectedProduct ? products.find(p => p.id === selectedProduct)?.totalStock || 0 : 0;
  const allocatedUnits = Object.values(allocations).reduce((a, b) => a + b, 0);
  const remainingUnits = totalStock - allocatedUnits;

  const handleAllocationChange = (key, value) => {
    const newValue = parseInt(value) || 0;
    const otherAllocations = Object.entries(allocations)
      .filter(([k]) => k !== key)
      .reduce((sum, [, val]) => sum + val, 0);

    if (newValue + otherAllocations > totalStock) {
      toast.error('Cannot allocate more units than available stock');
      return;
    }

    setAllocations(prev => ({
      ...prev,
      [key]: newValue
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-4xl font-bold mb-8">Inventory Management</h1>
      <div className="bg-white rounded-xl p-8 shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Allocate Inventory</h2>
        
        <div className="mb-8">
          <label className="block text-lg font-medium text-gray-700 mb-2">
            Select Product
          </label>
          <select
            value={selectedProduct}
            onChange={(e) => {
              setSelectedProduct(e.target.value);
              setAllocations({
                amazonUAE: 0,
                amazonSaudi: 0,
                amazonEgypt: 0,
                noonUAE: 0,
                noonSaudi: 0,
                noonEgypt: 0,
                jumia: 0,
                carrefourUAE: 0
              });
            }}
            className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          >
            <option value="">Choose a product</option>
            {products.map(product => (
              <option key={product.id} value={product.id}>
                {product.title} ({product.sku})
              </option>
            ))}
          </select>
        </div>

        {selectedProduct && (
          <>
            <div className="flex justify-between mb-6">
              <div className="text-lg">Total Stock: {totalStock} units</div>
              <div className="text-lg">Remaining Units: {remainingUnits}</div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {Object.entries({
                'Amazon UAE': 'amazonUAE',
                'Amazon Saudi': 'amazonSaudi',
                'Amazon Egypt': 'amazonEgypt',
                'Noon UAE': 'noonUAE',
                'Noon Saudi': 'noonSaudi',
                'Noon Egypt': 'noonEgypt',
                'Jumia': 'jumia',
                'Carrefour UAE': 'carrefourUAE'
              }).map(([label, key]) => (
                <div key={key}>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    {label} (units)
                  </label>
                  <input
                    type="number"
                    value={allocations[key]}
                    onChange={(e) => handleAllocationChange(key, e.target.value)}
                    className="w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    min="0"
                    max={totalStock}
                  />
                </div>
              ))}
            </div>

            <div className="mt-8">
              <button
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                disabled={remainingUnits < 0}
              >
                Save Allocation
              </button>
              {remainingUnits < 0 && (
                <p className="mt-2 text-red-600">
                  Over-allocated by {Math.abs(remainingUnits)} units
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InventoryManagement;